import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import Loader from "../common/Loader";
import {
  getMetaIntegration,
  getMetaOAuthUrl,
  getCampaignMetrics,
  getAdsTable,
  CampaignMetricsResponse,
  AdTableRow,
} from "../../services/meta/metaService";
import { BarChart3, ArrowRight, Users, Eye, MousePointerClick, Video, Megaphone, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import toast from "react-hot-toast";
import AdDetailModal from "./AdDetailModal";
import PlatformIcon from "./PlatformIcon";
import StatusBadge from "./StatusBadge";

const formatDateToYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultDateRange = (): { start: string; end: string } => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { start: formatDateToYMD(start), end: formatDateToYMD(end) };
};

interface CampaignsTabContentProps {
  dateStart?: string;
  dateEnd?: string;
  refreshKey?: number;
  selectedAdAccount?: string | null;
}

const CampaignsTabContent = ({ dateStart: externalDateStart, dateEnd: externalDateEnd, refreshKey, selectedAdAccount }: CampaignsTabContentProps) => {
  const { language } = useAuth();

  const [metrics, setMetrics] = useState<CampaignMetricsResponse | null>(null);
  const [adsTable, setAdsTable] = useState<AdTableRow[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const dateStart = externalDateStart || getDefaultDateRange().start;
  const dateEnd = externalDateEnd || getDefaultDateRange().end;
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string>("spend");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [metaConnected, setMetaConnected] = useState<boolean | null>(null);
  const [selectedAd, setSelectedAd] = useState<AdTableRow | null>(null);
  const [showPaused, setShowPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check if Meta is connected on mount
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const meta = await getMetaIntegration();
        setMetaConnected(meta?.is_active ?? false);
      } catch {
        setMetaConnected(false);
      }
    };
    checkConnection();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Load data when account, dates, or refresh key change
  useEffect(() => {
    if (!selectedAdAccount) return;
    loadData(selectedAdAccount, dateStart, dateEnd);
  }, [selectedAdAccount, dateStart, dateEnd, refreshKey]);

  const loadData = async (adAccountId: string, start: string, end: string) => {
    setLoadingData(true);
    setMetrics(null);
    setAdsTable([]);
    try {
      const [metricsResult, adsResult] = await Promise.allSettled([
        getCampaignMetrics(adAccountId, start, end),
        getAdsTable(adAccountId, start, end),
      ]);
      if (metricsResult.status === "fulfilled") setMetrics(metricsResult.value);
      if (adsResult.status === "fulfilled") setAdsTable(adsResult.value.ads || []);
      if (metricsResult.status === "rejected" && adsResult.status === "rejected") {
        toast.error(dictionary["campaigns-load-error"][language]);
      }
    } catch {
      toast.error(dictionary["campaigns-load-error"][language]);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const handleConnectMeta = async () => {
    setConnectError(null);
    setConnecting(true);
    try {
      const response = await getMetaOAuthUrl();
      const popup = window.open(
        response.authorizationUrl,
        "meta-oauth",
        "width=600,height=700,scrollbars=yes"
      );

      intervalRef.current = setInterval(async () => {
        if (popup?.closed) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          try {
            const meta = await getMetaIntegration();
            if (meta?.is_active) {
              setConnecting(false);
              setMetaConnected(true);
              toast.success(dictionary["integration-added"][language]);
              window.location.reload();
              return;
            }
          } catch {
            // Integration not found
          }
          setConnecting(false);
          setConnectError(dictionary["meta-connect-error"][language]);
        }
      }, 500);
    } catch {
      setConnecting(false);
      setConnectError(dictionary["meta-connect-error"][language]);
    }
  };

  const visibleAds = showPaused
    ? adsTable
    : adsTable.filter((a) => a.effective_status === "ACTIVE");
  const pausedCount = adsTable.length - adsTable.filter((a) => a.effective_status === "ACTIVE").length;
  const inactiveVisibleCount = visibleAds.filter((a) => a.effective_status !== "ACTIVE").length;

  const SEMAFORO_ORDER: Record<string, number> = { red: 0, yellow: 1, green: 2 };
  const sortedAds = [...visibleAds].sort((a, b) => {
    const aVal = (a as any)[sortColumn] ?? 0;
    const bVal = (b as any)[sortColumn] ?? 0;
    if (sortColumn === "semaforo") {
      const aRank = SEMAFORO_ORDER[aVal] ?? 99;
      const bRank = SEMAFORO_ORDER[bVal] ?? 99;
      return sortDirection === "asc" ? aRank - bRank : bRank - aRank;
    }
    if (typeof aVal === "string") {
      return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
  });

  // Empty state: not connected to Meta
  if (metaConnected === false) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <BarChart3 className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          {dictionary["campaigns-empty-title"][language]}
        </h2>
        <p className="text-sm text-gray-500 mb-6 text-center max-w-md">
          {dictionary["campaigns-empty-description"][language]}
        </p>
        {connectError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 max-w-md w-full text-center">
            {connectError}
          </div>
        )}
        <button
          className="flex items-center gap-2 px-6 py-3 bg-[#0081FB] text-white rounded-lg hover:bg-[#006AD4] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleConnectMeta}
          disabled={connecting}
        >
          {connecting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              {dictionary["campaigns-connect-meta"][language]}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    );
  }

  // Still loading connection status
  if (metaConnected === null) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader />
      </div>
    );
  }

  // Connected but no account selected — prompt to select
  if (!selectedAdAccount) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
          <BarChart3 className="w-8 h-8 text-blue-400" />
        </div>
        <p className="text-sm text-gray-500 text-center max-w-md">
          {dictionary["campaigns-select-account-empty"][language]}
        </p>
      </div>
    );
  }

  const hasActiveCampaigns = (metrics?.running_campaigns?.length ?? 0) > 0;

  // Single loading state for both metrics + ads
  if (loadingData) {
    return (
      <div className="flex justify-center py-20">
        <Loader />
      </div>
    );
  }

  // Empty state — no ads at all for this account
  if (!loadingData && adsTable.length === 0 && !metrics) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Megaphone className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          {dictionary["campaigns-no-active"][language]}
        </h2>
        <p className="text-sm text-gray-500 text-center max-w-md">
          {dictionary["campaigns-no-active-description"][language]}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Date label */}

      {/* Metrics cards */}
      {metrics && (
        <>
          {hasActiveCampaigns && (
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
                {dictionary["campaigns-active"][language]} ({metrics.running_campaigns.length})
              </h2>
              <div className="flex flex-wrap gap-2">
                {metrics.running_campaigns.map((name, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-full border border-green-200">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <MetricCard icon={<Users className="w-5 h-5 text-blue-600" />} label={dictionary["campaigns-metric-reach"][language]} value={formatNumber(metrics.reach ?? 0)} />
            <MetricCard icon={<Eye className="w-5 h-5 text-purple-600" />} label={dictionary["campaigns-metric-impressions"][language]} value={formatNumber(metrics.impressions ?? 0)} />
            <MetricCard icon={<MousePointerClick className="w-5 h-5 text-orange-600" />} label={dictionary["campaigns-metric-clicks"][language]} value={formatNumber(metrics.link_clicks ?? 0)} />
            <MetricCard icon={<Video className="w-5 h-5 text-teal-600" />} label={dictionary["campaigns-metric-video-3s"][language]} value={formatNumber(metrics.video_3s ?? 0)} />
            <MetricCard icon={<Video className="w-5 h-5 text-indigo-600" />} label={dictionary["campaigns-metric-video-50"][language]} value={formatNumber(metrics.video_50 ?? 0)} />
            <MetricCard icon={<BarChart3 className="w-5 h-5 text-gray-600" />} label={dictionary["campaigns-metric-frequency"][language]} value={(metrics.frequency ?? 0).toFixed(2)} />
          </div>
        </>
      )}

      {/* Ads Table — simplified columns, details open in modal */}
      {adsTable.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-24">
          {/* Filter bar */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 text-xs text-gray-500">
            <span>
              <strong className="text-gray-700">{sortedAds.length}</strong> {dictionary["campaigns-ads-count"][language]}
              {showPaused && inactiveVisibleCount > 0 && (
                <> · {inactiveVisibleCount} {dictionary["campaigns-inactive-included"][language]}</>
              )}
              {!showPaused && pausedCount > 0 && (
                <> · {pausedCount} {dictionary["campaigns-paused-hidden"][language]}</>
              )}
            </span>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showPaused}
                onChange={(e) => setShowPaused(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-fluxi focus:ring-fluxi"
              />
              <span>{dictionary["campaigns-show-paused"][language]}</span>
            </label>
          </div>
          <div className="overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <SortableHeader column="ad_name" label={dictionary["campaigns-col-ad"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader column="platform" label={dictionary["campaigns-col-platform"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader column="funnel_name" label={dictionary["campaigns-col-funnel"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader column="spend" label={dictionary["campaigns-col-spend"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader column="results" label={dictionary["campaigns-col-results"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader column="cost_per_result" label={dictionary["campaigns-col-cpa"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                  <SortableHeader column="semaforo" label={dictionary["campaigns-col-status-ai"][language]} currentSort={sortColumn} direction={sortDirection} onSort={handleSort} />
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {dictionary["campaigns-col-detail"][language]}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAds.map((ad) => (
                  <tr key={ad.ad_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-3 text-left font-medium text-gray-800 max-w-[240px] truncate">{ad.ad_name}</td>
                    <td className="px-3 py-3 text-left">
                      <PlatformIcon platform={ad.platform} language={language} />
                    </td>
                    <td className="px-3 py-3 text-left max-w-[180px]">
                      {ad.funnel_id && ad.destination_url ? (
                        <a
                          href={ad.destination_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-fluxi hover:underline truncate"
                        >
                          <span className="truncate">{ad.funnel_name ?? ad.funnel_id}</span>
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-left">{formatCurrency(ad.spend)}</td>
                    <td className="px-3 py-3 text-left">{formatNumber(ad.results)}</td>
                    <td className="px-3 py-3 text-left">{formatCurrency(ad.cost_per_result)}</td>
                    <td className="px-3 py-3 text-left">
                      <StatusBadge status={ad.semaforo} language={language} />
                    </td>
                    <td className="px-3 py-3 text-left">
                      <button
                        type="button"
                        onClick={() => setSelectedAd(ad)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <Eye className="w-3 h-3" />
                        {dictionary["common-view"][language]}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {selectedAd && selectedAdAccount && (
        <AdDetailModal
          ad={selectedAd}
          adAccountId={selectedAdAccount}
          dateStart={externalDateStart}
          dateEnd={externalDateEnd}
          language={language}
          onClose={() => setSelectedAd(null)}
        />
      )}
    </div>
  );
};

const SortableHeader = ({
  column, label, currentSort, direction, onSort,
}: {
  column: string; label: string; currentSort: string; direction: "asc" | "desc"; onSort: (col: string) => void;
}) => (
  <th
    className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 whitespace-nowrap"
    onClick={() => onSort(column)}
  >
    <div className="flex items-center gap-1">
      {label}
      {currentSort === column && (
        direction === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
      )}
    </div>
  </th>
);

const MetricCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="bg-white p-4 rounded-lg border border-gray-200">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-xs text-gray-500 font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
  </div>
);

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toLocaleString();
};

const formatCurrency = (num: number): string => {
  if (num === 0) return "$0";
  if (num < 0) return "-$" + Math.abs(num).toFixed(2);
  if (num < 0.01) return "<$0.01";
  return "$" + num.toFixed(2);
};

export default CampaignsTabContent;
